import React, { useState, useCallback, useEffect } from 'react';
import '../../../../App.css';
import '../../Employees.css';
import { domain, token } from '../../../Security';
import axios from '../../../../api/apiClient';
import { Button, CreateTableNew as CreateTable, GeneralMessage, useGeneralMessage } from '../../../../common/APICommon';
import { AddEditFormMemo, DeleteForm } from '../EmployeeFormFld/MemoForm';

import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import { LoadingSpinner } from '../../../../common/Loading';

export function MemoTab({ viewedData, isEditMode }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);

  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage(5000);

  const [memoTypes, setMemoTypes] = useState([]);
  const fetchMemoTypes = useCallback(async () => {
    try {
      const response = await axios.get(domain + "/api/MemoType", {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setMemoTypes(response.data.filter(mt => mt.isActive === true));
    } catch (error) {
      console.error('Error fetching memo types:', error);
    }
  }, []);
  useEffect(() => { fetchMemoTypes(); }, [fetchMemoTypes]);

  const headers = [
    "Memo Type",
    "Date From",
    "Date To",
    "Description",
    "CC",
    "Image",
    "Is Active?",
    ...(isEditMode ? ["Actions"] : [])
  ];
  const accessors = [
    "memoTypeName",
    "dateFrom",
    "dateTo",
    "description",
    "ccCount",
    "content",
    "isActive",
    ...(isEditMode ? ["actions"] : [])
  ];
  const [currentPage, setCurrentPage] = useState(1);

  const [memoTypeId, setMemoTypeId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [ccList, setCcList] = useState([]);
  const [isActive, setIsActive] = useState(true);

  const resetLocalVariable = () => {
    setMemoTypeId("");
    setDateFrom("");
    setDateTo("");
    setDescription("");
    setContent(null);
    setSelectedData(null);
    setCcList([]);
    setIsActive(true);
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const fetchData = useCallback(async () => {
    const apiUrl = domain + `/api/EmployeeMemo/employee/${viewedData.id}`;
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const enriched = response.data.map(memo => ({
        ...memo,
        ccCount: memo.ccList?.length > 0 ? `${memo.ccList.length} person(s)` : "—"
      }));
      setData(enriched);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [viewedData]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Custom cell renderer to fix description overflow ─────────────────────
  const customCellRenderer = useCallback((row, accessor) => {
    if (accessor === "description") {
      return (
        <div
          className="max-w-[200px] max-h-[60px] overflow-y-auto whitespace-normal break-words text-sm text-gray-600"
          title={row.description}
        >
          {row.description ?? "-"}
        </div>
      );
    }
    return null; // fall back to default rendering for all other columns
  }, []);
  // ──────────────────────────────────────────────────────────────────────────

  // --- ADD ---
  const [showAddForm, setShowAddForm] = useState(false);

  const openAddForm = () => { resetLocalVariable(); setShowAddForm(true); };
  const closeAddForm = () => { setShowAddForm(false); resetLocalVariable(); };

  const handleSubmitMemo = async (event) => {
    event.preventDefault();
    const apiUrl = domain + "/api/EmployeeMemo";

    let photoToBeSave = null;
    if (content) {
      const base64String = await toBase64(content);
      photoToBeSave = base64String.split(',')[1];
    }

    const payload = {
      employeePersonalInformationId: viewedData.id,
      memoTypeId: memoTypeId ? parseInt(memoTypeId) : null,
      dateFrom,
      dateTo,
      description,
      content: photoToBeSave,
      ccEmployeeIds: ccList.map(e => e.id),
      isActive: isActive,
    };

    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, payload, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      await fetchData();
      closeAddForm();
      showGeneralMessage("Memo added successfully!", true);
    } catch (error) {
      console.error('There was a problem adding memo:', error);
      const errorMessage = error.response?.data?.message ?? "Failed to add memo.";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  // --- EDIT ---
  const [showEditForm, setShowEditForm] = useState(false);

  const openEditForm = () => setShowEditForm(true);
  const closeEditForm = () => { setShowEditForm(false); resetLocalVariable(); };

  const editHandleClick = (rowData) => {
    setSelectedData(rowData);
    setMemoTypeId(rowData.memoTypeId ? rowData.memoTypeId.toString() : "");
    setDateFrom(rowData.dateFrom ? rowData.dateFrom.substring(0, 10) : "");
    setDateTo(rowData.dateTo ? rowData.dateTo.substring(0, 10) : "");
    setDescription(rowData.description ?? "");
    setContent(null);
    setCcList(
      (rowData.ccList ?? []).map(cc => ({
        id: cc.id,
        fullName: cc.employeeFullName,
        employeeId: cc.employeeId
      }))
    );
    setIsActive(rowData.isActive ?? true);
    openEditForm();
  };

  const handleUpdateMemo = async (event) => {
    event.preventDefault();
    const apiUrl = domain + "/api/EmployeeMemo";

    let photoToBeSave = selectedData?.content ?? null;
    if (content) {
      const base64String = await toBase64(content);
      photoToBeSave = base64String.split(',')[1];
    }

    const payload = {
      employeePersonalInformationId: viewedData.id,
      memoTypeId: memoTypeId ? parseInt(memoTypeId) : null,
      dateFrom,
      dateTo,
      description,
      content: photoToBeSave,
      ccEmployeeIds: ccList.map(e => e.id),
      isActive: isActive,
    };

    try {
      setLoadingRequest(true);
      await axios.put(apiUrl + `/${selectedData.id}`, payload, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      await fetchData();
      closeEditForm();
      showGeneralMessage("Memo updated successfully!", true);
    } catch (error) {
      console.error('There was a problem updating memo:', error);
      const errorMessage = error.response?.data?.message ?? "Failed to update memo.";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  return (
    <>
      <div className="family-tab-top" style={{ display: isEditMode ? "flex" : "none", justifyContent: "start" }}>
        <Button
          icon={<NoteAddIcon />}
          name={"ADD"}
          className={"bg-green-500 hover:bg-green-600 text-white mb-2"}
          onClick={openAddForm}
        />
      </div>

      <div className="family-tab-body">
        <div className="employeesTab-table container">
          <CreateTable
            tableName={"EmployeeMemo"}
            headers={headers}
            accessors={accessors}
            data={data}
            actions={isEditMode ? [
              {
                name: "Edit",
                tooltip: "edit",
                icon: <EditIcon fontSize="small" />,
                className: "bg-green-500 hover:bg-green-600 flex items-center",
                function: editHandleClick,
                visible: true,
              },
            ] : []}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={10}
            totalItems={data?.length}
            customCellRenderer={customCellRenderer}
          />
        </div>
      </div>

      {/* ADD */}
      <AddEditFormMemo
        isOpen={showAddForm}
        onClose={closeAddForm}
        memoTypeId={memoTypeId} setMemoTypeId={setMemoTypeId}
        memoTypes={memoTypes}
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        description={description} setDescription={setDescription}
        content={content} setContent={setContent}
        ccList={ccList} setCcList={setCcList}
        isActive={isActive} setIsActive={setIsActive}
        handleSubmit={handleSubmitMemo}
        modalName="Add Memo"
      />

      {/* EDIT */}
      <AddEditFormMemo
        isOpen={showEditForm}
        onClose={closeEditForm}
        memoTypeId={memoTypeId} setMemoTypeId={setMemoTypeId}
        memoTypes={memoTypes}
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        description={description} setDescription={setDescription}
        content={content} setContent={setContent}
        existingImage={selectedData?.content ?? null}
        ccList={ccList} setCcList={setCcList}
        isActive={isActive} setIsActive={setIsActive}
        handleSubmit={handleUpdateMemo}
        modalName="Update Memo"
      />

      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
      <LoadingSpinner loading={loadingRequest || loading} />
    </>
  );
}